import { z } from 'zod';
import { AuditActorRoleSchema } from '../common/audit.enums';
import { PaginationResponseSchema } from '../common/common.schemas';
import { BookingAuditAction } from './booking.enums';

export const BookingAuditActorSchema = z.object({
  id: z.string(),
  role: AuditActorRoleSchema,
});

/** Optional request metadata copied into admin `details` when present. */
export const BookingAuditRequestMetadataSchema = z.object({
  reason: z.string().optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  traceId: z.string().optional(),
});

/** Snapshot blob stored in audit `before` / `after` JSON columns. */
export const BookingAuditSnapshotSchema = z.record(z.string(), z.unknown());

/**
 * STATE_CHANGED, TERMS_MODIFIED, and booking lifecycle actions surface
 * `beforeState` / `afterState` via admin formatters.
 */
export const BookingAuditStateTransitionDetailsSchema = BookingAuditRequestMetadataSchema.extend({
  beforeState: BookingAuditSnapshotSchema.optional(),
  afterState: BookingAuditSnapshotSchema.optional(),
});

export const BookingAuditMessageSentDetailsSchema = BookingAuditRequestMetadataSchema.extend({
  messageId: z.string(),
  bodyPreview: z.string(),
  attachmentCount: z.number().int().nonnegative().optional(),
});

export const BookingAuditAttachmentAddedDetailsSchema = BookingAuditRequestMetadataSchema.extend({
  messageId: z.string(),
  attachmentId: z.string(),
  url: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});

const auditEntryBase = {
  timestamp: z.string().datetime(),
  actor: BookingAuditActorSchema,
};

function stateTransitionAuditEntry<
  A extends (typeof BookingAuditAction)[keyof typeof BookingAuditAction],
>(action: A) {
  return z.object({
    ...auditEntryBase,
    action: z.literal(action),
    details: BookingAuditStateTransitionDetailsSchema,
  });
}

/** One audit row in `GET /admin/bookings/:id/audit` — discriminated by `action`. */
export const AdminBookingAuditEntrySchema = z.discriminatedUnion('action', [
  stateTransitionAuditEntry(BookingAuditAction.STATE_CHANGED),
  stateTransitionAuditEntry(BookingAuditAction.TERMS_MODIFIED),
  stateTransitionAuditEntry(BookingAuditAction.BOOKING_CREATED),
  stateTransitionAuditEntry(BookingAuditAction.BOOKING_COMPLETED),
  stateTransitionAuditEntry(BookingAuditAction.BOOKING_CANCELLED),
  stateTransitionAuditEntry(BookingAuditAction.BOOKING_ACCEPTED),
  stateTransitionAuditEntry(BookingAuditAction.BOOKING_REJECTED),
  z.object({
    ...auditEntryBase,
    action: z.literal(BookingAuditAction.MESSAGE_SENT),
    details: BookingAuditMessageSentDetailsSchema,
  }),
  z.object({
    ...auditEntryBase,
    action: z.literal(BookingAuditAction.ATTACHMENT_ADDED),
    details: BookingAuditAttachmentAddedDetailsSchema,
  }),
]);

export const AdminBookingAuditResponseSchema = z.object({
  items: z.array(AdminBookingAuditEntrySchema),
  ...PaginationResponseSchema.shape,
});
